import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const RentalRequestModal = ({ isOpen, onClose, truck, onSuccess, language = 'pt-BR' }) => {
  const [loading, setLoading] = useState(false);
  const [hasEndDate, setHasEndDate] = useState(false);
  const [formData, setFormData] = useState({
    data_inicio: '',
    data_fim: '',
    proposito: ''
  });
  const [dataInicioAlterada, setDataInicioAlterada] = useState(false);

  // Reset quando modal abre
  React.useEffect(() => {
    if (isOpen) {
      const hoje = new Date().toISOString().split('T')[0];
      setFormData({
        data_inicio: hoje,
        data_fim: '',
        proposito: ''
      });
      setDataInicioAlterada(false);
      setHasEndDate(false);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!formData.data_inicio) {
      toast.error(language === 'pt-BR' ? 'Selecione a data de início' : 'Select start date');
      return;
    }

    // Verificar se data de início não foi alterada
    const hoje = new Date().toISOString().split('T')[0];
    if (!dataInicioAlterada && formData.data_inicio === hoje) {
      const confirmacao = window.confirm(
        language === 'pt-BR' 
          ? 'Data de início não alterada. Tem certeza que deseja deixar a data de início para hoje?'
          : 'Start date not changed. Are you sure you want to keep the start date as today?'
      );
      
      if (!confirmacao) {
        return; // Cancela e volta para o modal
      }
    }

    setLoading(true);
    try {
      const token = sessionStorage.getItem('access_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;

      await axios.post(`${API_URL}/api/rentals/request`, {
        truck_id: truck.id,
        data_inicio: formData.data_inicio,
        data_fim: hasEndDate ? formData.data_fim : null,
        proposito: formData.proposito
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(language === 'pt-BR' ? '✅ Solicitação enviada! Aguarde aprovação.' : '✅ Request sent! Wait for approval.');
      onSuccess();
      onClose();
      setFormData({ data_inicio: '', data_fim: '', proposito: '' });
      setDataInicioAlterada(false);
    } catch (error) {
      console.error('Erro:', error);
      toast.error(error.response?.data?.detail || (language === 'pt-BR' ? 'Erro ao enviar solicitação' : 'Error sending request'));
    } finally {
      setLoading(false);
    }
  };

  if (!truck) return null;

  // Calcular estimativa se tem data fim
  const calcularEstimativa = () => {
    if (!formData.data_inicio || !formData.data_fim) return null;
    const inicio = new Date(formData.data_inicio);
    const fim = new Date(formData.data_fim);
    const dias = Math.floor((fim - inicio) / (1000 * 60 * 60 * 24)) + 1;
    return dias > 0 ? dias * 100 : null;
  };

  const estimativa = calcularEstimativa();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>🚛 {language === 'pt-BR' ? 'Solicitar Aluguel' : 'Request Rental'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-gray-50 p-3 rounded">
            <p className="font-semibold">{truck.modelo}</p>
            <p className="text-sm text-gray-600">{truck.placa} • {truck.cor} • {truck.ano}</p>
          </div>

          <div>
            <Label>{language === 'pt-BR' ? 'Data de Início' : 'Start Date'} *</Label>
            <Input 
              type="date"
              value={formData.data_inicio}
              onChange={(e) => {
                setFormData({...formData, data_inicio: e.target.value});
                setDataInicioAlterada(true);
              }}
              min={new Date().toISOString().split('T')[0]}
            />
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <input 
                type="checkbox"
                id="has-end-date"
                checked={hasEndDate}
                onChange={(e) => {
                  setHasEndDate(e.target.checked);
                  if (!e.target.checked) {
                    setFormData({...formData, data_fim: ''});
                  }
                }}
                className="w-4 h-4"
              />
              <Label htmlFor="has-end-date" className="cursor-pointer">
                {language === 'pt-BR' ? 'Definir data de devolução' : 'Set return date'}
              </Label>
            </div>
            
            {hasEndDate && (
              <div>
                <Label>{language === 'pt-BR' ? 'Data de Devolução' : 'Return Date'}</Label>
                <Input 
                  type="date"
                  value={formData.data_fim}
                  onChange={(e) => setFormData({...formData, data_fim: e.target.value})}
                  min={formData.data_inicio}
                />
              </div>
            )}
            
            {!hasEndDate && (
              <p className="text-xs text-gray-500 italic">
                {language === 'pt-BR' 
                  ? '📅 Sem data definida - Valor será calculado na devolução' 
                  : '📅 No date set - Value will be calculated at return'}
              </p>
            )}
          </div>

          {estimativa && (
            <div className="bg-green-50 p-3 rounded border border-green-200">
              <p className="text-sm text-green-800">
                💰 {language === 'pt-BR' ? 'Valor estimado:' : 'Estimated value:'} <strong>${estimativa}</strong>
              </p>
              <p className="text-xs text-green-600 mt-1">
                {Math.floor(estimativa / 100)} {language === 'pt-BR' ? 'dias × $100/dia' : 'days × $100/day'}
              </p>
            </div>
          )}

          <div>
            <Label>{language === 'pt-BR' ? 'Propósito (opcional)' : 'Purpose (optional)'}</Label>
            <Textarea 
              value={formData.proposito}
              onChange={(e) => setFormData({...formData, proposito: e.target.value})}
              placeholder={language === 'pt-BR' ? 'Para que vai usar o caminhão?' : 'What will you use the truck for?'}
              rows={3}
            />
          </div>

          <div className="bg-blue-50 p-3 rounded text-sm">
            <p className="font-semibold text-blue-800 mb-1">ℹ️ {language === 'pt-BR' ? 'Informações:' : 'Information:'}</p>
            <ul className="text-blue-700 space-y-1 text-xs ml-4 list-disc">
              <li>{language === 'pt-BR' ? 'Valor: $100 por dia' : 'Rate: $100 per day'}</li>
              <li>{language === 'pt-BR' ? 'Aguarde aprovação do Admin' : 'Wait for Admin approval'}</li>
              <li>{language === 'pt-BR' ? 'Valor final calculado na devolução' : 'Final value calculated at return'}</li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {language === 'pt-BR' ? 'Cancelar' : 'Cancel'}
          </Button>
          <Button onClick={handleSubmit} disabled={loading} className="bg-green-600 hover:bg-green-700">
            {loading ? (language === 'pt-BR' ? 'Enviando...' : 'Sending...') : (language === 'pt-BR' ? '📤 Enviar Solicitação' : '📤 Send Request')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RentalRequestModal;